import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useCollections } from "@/context/CollectionsContext";
import { FREE_SHIP_THRESHOLD } from "@/context/CartContext";

const HERO = "https://images.unsplash.com/photo-1545151414-8a948e1ea54f";
const TILE_IMAGES = [
  "https://images.pexels.com/photos/8068701/pexels-photo-8068701.jpeg",
  "https://images.pexels.com/photos/8007208/pexels-photo-8007208.jpeg",
  "https://images.unsplash.com/photo-1599280174407-fdc3e8c47856",
];
const FALLBACK_TILES = [
  { name: "Accessories", to: "/shop/accessories", img: TILE_IMAGES[0] },
  { name: "Best Sellers", to: "/shop/best-sellers", img: TILE_IMAGES[1] },
  { name: "Shop All", to: "/shop/all", img: TILE_IMAGES[2] },
];

// Branded lifestyle imagery per collection (falls back to stock pool if unmapped)
const COLLECTION_IMAGES = {
  "accessories": "https://customer-assets-jai6qajn.emergentagent.net/job_rock-hard-tennis/artifacts/f5oxjxdo_hf_20260625_163006_a9555bdf-7af6-46a0-81e0-18fc2b4d7f5f%20%282%29.webp",
  "best-sellers": "https://customer-assets-jai6qajn.emergentagent.net/job_rock-hard-tennis/artifacts/g2oktmbf_hf_20260625_181813_0893de35-517b-4206-99bc-d35b5a1fe7b4%20%282%29.webp",
  "teams": "https://customer-assets-jai6qajn.emergentagent.net/job_rock-hard-tennis/artifacts/dynurwz5_hf_20260630_220350_5f4cada1-7a71-4750-aa20-b557080142c6%20%282%29.webp",
  "shop-men": "https://customer-assets-jai6qajn.emergentagent.net/job_rock-hard-tennis/artifacts/t31jrrlt_hf_20260721_135340_e6f98a60-ee41-4c0d-b6bb-6feb7918cfd3.webp",
  "shop-women": "https://customer-assets-jai6qajn.emergentagent.net/job_rock-hard-tennis/artifacts/8k5kfgdp_hf_20260625_181813_0893de35-517b-4206-99bc-d35b5a1fe7b4%20%282%29.webp",
  "performance-shirts": "https://customer-assets-jai6qajn.emergentagent.net/job_rock-hard-tennis/artifacts/gyw1x93i_hf_20260721_140013_c1d4d76a-c59a-4c2d-bdb4-32f448467bb2.webp",
};

const MarqueeBar = () => {
  const text = [`FREE SHIPPING OVER $${FREE_SHIP_THRESHOLD}`, "SWING HARD", "NEW DROP LIVE", "TENNIS GEAR WITH ATTITUDE", "PLAY HARD. NO SOFT."];
  const repeated = [...text, ...text, ...text, ...text];
  return (
    <div className="bg-sale text-white overflow-hidden py-2.5 whitespace-nowrap">
      <div className="marquee-track inline-flex gap-8 will-change-transform">
        {repeated.map((t, i) => (
          <span key={i} className="text-xs font-bold uppercase tracking-[0.25em] flex items-center gap-8">
            {t} <span className="opacity-60">/</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const collections = useCollections();

  useEffect(() => {
    api.get("/products?exclude=teams").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const featured = products.slice(0, 8);
  const tiles = collections.length
    ? collections.slice(0, 6).map((c, i) => ({ name: c.title, to: `/shop/${c.handle}`, img: COLLECTION_IMAGES[c.handle] || TILE_IMAGES[i % TILE_IMAGES.length] }))
    : FALLBACK_TILES;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] mt-20 overflow-hidden">
        <img src={HERO} alt="Tennis" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <p className="text-sale font-bold uppercase tracking-[0.3em] text-sm md:text-base mb-4 animate-fade-up opacity-0">Tennis Gear With Attitude</p>
          <h1 className="font-display text-white text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.85] uppercase animate-fade-up opacity-0" style={{ animationDelay: "100ms" }}>
            Swing<br />Hard.
          </h1>
          <p className="text-white/80 max-w-md mt-6 text-base animate-fade-up opacity-0" style={{ animationDelay: "200ms" }}>
            Tennis gear built for players who don't hold back. Own every point.
          </p>
          <div className="flex gap-4 mt-8 animate-fade-up opacity-0" style={{ animationDelay: "300ms" }}>
            <Link to="/shop/all" className="bg-cream text-ink px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors" data-testid="hero-shop-all">Shop All</Link>
            <Link to="/shop/accessories" className="bg-transparent border border-cream text-cream px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-cream hover:text-ink transition-colors" data-testid="hero-accessories">Accessories</Link>
          </div>
        </div>
      </section>

      <MarqueeBar />

      {/* FEATURED */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex justify-between items-end mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">Best Sellers</h2>
          <Link to="/shop/all" className="text-xs font-bold uppercase tracking-widest border-b border-ink pb-1 hover:text-sale hover:border-sale transition-colors" data-testid="view-all">View All</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-12">
          {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-8 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiles.map((c) => (
            <Link key={c.name} to={c.to} className="relative aspect-[3/4] overflow-hidden group" data-testid={`category-${c.name.replace(/\s/g, "-").toLowerCase()}`}>
              <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/25 group-hover:bg-black/45 transition-colors duration-500" />
              <div className="absolute bottom-8 left-8 z-10">
                <h3 className="text-cream font-display text-4xl uppercase">{c.name}</h3>
                <span className="text-cream/80 text-xs font-bold uppercase tracking-widest border-b border-cream/60 pb-1">Shop Now</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PURPOSE */}
      <section className="relative">
        <img src="https://images.unsplash.com/photo-1554068865-24cecd4e34b8" alt="Tennis court" className="w-full h-[60vh] object-cover" />
        <div className="absolute inset-0 bg-ink/70 flex items-center">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 w-full">
            <div className="max-w-xl">
              <h2 className="font-display text-cream text-4xl md:text-5xl uppercase mb-6 leading-tight">No Soft Serves.</h2>
              <p className="text-cream/80 text-base leading-relaxed">
                Rock Hard Tennis is for players who take the game seriously and themselves… not so much.
                Our shirts talk more trash than you do — and they never double-fault.
                No fancy fabrics, no ego, no chill — just funny gear that hits as hard as your trash talk.
                Play hard. No soft.
              </p>
              <Link to="/shop/all" className="inline-block mt-8 bg-sale text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-sale/85 transition-colors" data-testid="purpose-cta">
                Gear Up
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
